import { useEffect, lazy, Suspense } from 'react'
import { Layout } from './components/Layout'
import { ARLoadingFallback } from './components/ARLoadingFallback'
import { usePWA, useFirebaseAnalytics } from './hooks'
import { ErrorFallback, ErrorBoundary } from './components/ErrorBoundary'
import { APP_VERSION } from './utils/version'

// 🚀 Lazy loading de ARScene (MindAR + Three.js son pesados, solo cargar cuando se necesiten)
const ARScene = lazy(() =>
  import('./components/ARScene').then(module => ({ default: module.ARScene }))
)

/**
 * Componente principal de la aplicación con soporte para PWA y monitoreo de errores
 * @component
 */
const App = () => {
  // 🔥 Firebase Analytics
  const analytics = useFirebaseAnalytics()

  // Configurar PWA con auto-actualización cada 5 minutos
  usePWA({
    updateInterval: 60000 * 5, // 5 minutos
    autoUpdate: true,
    onUpdateFound: () => {
      console.log('🆕 Nueva versión detectada, actualizando...')
      analytics.trackEvent('pwa_update_found', {
        update_type: 'auto',
        app_version: APP_VERSION,
      })
    },
  })

  // Registrar vista de página inicial y configurar propiedades de usuario
  useEffect(() => {
    analytics.trackPageView('/', 'Home MenuAR')

    // Configurar propiedades enriquecidas de usuario
    analytics.setUserProps({
      app_version: APP_VERSION, // Versión inyectada desde package.json en build time
      device_type: navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop',
      webgl_support: document.createElement('canvas').getContext('webgl2') ? 'webgl2' : 'webgl1',
      ar_capable: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      first_visit: new Date().toISOString(),
    })
  }, [analytics])

  return (
    <Layout>
      <ErrorBoundary fallback={<ErrorFallback />}>
        <Suspense fallback={<ARLoadingFallback />}>
          <ARScene />
        </Suspense>
      </ErrorBoundary>
    </Layout>
  )
}

export default App
